/* eslint-disable @reatom/unit-naming-rule */
import { atom } from '@reatom/framework'
import { useAction, useAtom, useCtx } from '@reatom/npm-react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled, { css } from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import {
  findScrollableAncestor,
  scrollAnchorToScrollportTop,
} from '@root/shared/lib/dom/find-scrollable-ancestor'
import { mergeRefs } from '@root/shared/react'

import { type DataTableModel } from '../atoms'

import { DataTableEmpty } from './data-table-empty'
import { DataTableHeader } from './data-table-header'
import { DataTableRow } from './data-table-row'
import { createColumnGridTemplate, type Column } from './data-table-types'
import { DataTableBackToTop } from './helper-components/data-table-back-to-top'

import type { DataTableContentProps } from './data-table-content'

type DataTableContentSimpleProps<
  D extends object,
  ColumnId extends string = string,
  Context extends object | undefined = undefined,
> = Omit<
  DataTableContentProps<D, ColumnId, Context>,
  'virtualization' | 'disableScroll' | 'virtuosoRef'
>

/** @deprecated Use DataTableContentVirtuoso+customScrollParent instead */
export const DataTableContentSimple = <
  D extends object,
  ColumnId extends string = string,
  Context extends object | undefined = undefined,
>({
  columns,
  model,
  rowHeight = 30,
  renderBackgroundLink,
  renderRow,
  getRowStyle,
  withBackToTop = true,
  floatingActions,
  onScroll,
  context,
  onRowClick,
  emptyTitle,
  renderError,
  renderEmptyMessage,
  stickyHeaderTop,
  minWidth,
  scrollContainerRef: scrollContainerRefProp,
  increaseViewportBy,
}: DataTableContentSimpleProps<D, ColumnId, Context>) => {
  const scrollContainerRefInternal = useRef<HTMLElement>(null)
  const tableBodyRef = useRef<HTMLDivElement>(null)
  const scrollContainerRef = mergeRefs([
    scrollContainerRefProp,
    scrollContainerRefInternal,
  ])
  const loadMoreSentinelRef = useRef<HTMLDivElement>(null)
  const scrollAtom = useMemo(() => atom(0), [])
  const ctx = useCtx()

  const [itemsState] = useAtom(model.overrideState ?? model)
  const [hasMore] = useAtom(model.hasMoreItems)
  const [loadMoreState] = useAtom(model.loadMoreState)
  const loadMore = useAction(model.loadMore)

  useEffect(() => {
    const element = scrollContainerRefInternal.current
    if (!element) {
      return
    }

    const scrollEl = findScrollableAncestor(element)
    if (!(scrollEl instanceof HTMLElement)) {
      return
    }

    const handleNativeScroll = () => {
      const tableRect = element.getBoundingClientRect()
      const scrollRect = scrollEl.getBoundingClientRect()
      const tableScrollOffset = Math.max(0, scrollRect.top - tableRect.top)
      scrollAtom(ctx, tableScrollOffset)
      onScroll?.({
        currentTarget: scrollEl,
      } as React.UIEvent<HTMLDivElement>)
    }

    handleNativeScroll()
    scrollEl.addEventListener('scroll', handleNativeScroll, { passive: true })

    return () => {
      scrollEl.removeEventListener('scroll', handleNativeScroll)
    }
  }, [ctx, itemsState, onScroll, scrollAtom])

  useEffect(() => {
    const sentinel = loadMoreSentinelRef.current
    if (!sentinel) {
      return
    }

    const scrollEl = findScrollableAncestor(sentinel)

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) {
          return
        }

        if (
          AsyncStateN.isLoading(loadMoreState) ||
          AsyncStateN.isError(loadMoreState) ||
          !hasMore
        ) {
          return
        }

        loadMore()
      },
      {
        root: scrollEl instanceof HTMLElement ? scrollEl : undefined,
        rootMargin: `${rowHeight}px`,
      },
    )

    observer.observe(sentinel)

    return () => {
      observer.disconnect()
    }
  }, [hasMore, itemsState, loadMore, loadMoreState, rowHeight])

  const handleBackToTop = () => {
    const tableEl = scrollContainerRefInternal.current
    if (!tableEl) {
      return
    }

    scrollAnchorToScrollportTop(tableEl, stickyHeaderTop)
  }

  const gridTemplateColumns = useMemo(
    () => createColumnGridTemplate(columns),
    [columns],
  )
  const rowStyle = useMemo(
    () => ({ gridTemplateColumns }),
    [gridTemplateColumns],
  )

  const data = itemsState.value?.data ?? []
  const resolvedRowCount = AsyncStateN.isSuccess(itemsState)
    ? // eslint-disable-next-line unicorn/no-nested-ternary
      hasMore
      ? itemsState.value.data.length + 1
      : itemsState.value.data.length
    : 0
  const isEmpty = resolvedRowCount === 0 || AsyncStateN.isLoading(itemsState)

  const overscan =
    increaseViewportBy === undefined
      ? VIRTUAL_ROW_OVERSCAN
      : Math.max(
          VIRTUAL_ROW_OVERSCAN,
          Math.ceil(increaseViewportBy / rowHeight),
        )

  const { startIndex, endIndex } = useVirtualizedRowRange({
    anchorRef: tableBodyRef,
    rowCount: resolvedRowCount,
    rowHeight,
    overscan,
    enabled: !isEmpty,
  })

  const topSpacerHeight = startIndex * rowHeight
  const bottomSpacerHeight = Math.max(
    0,
    (resolvedRowCount - endIndex - 1) * rowHeight,
  )
  const visibleRowIndices =
    endIndex >= startIndex
      ? Array.from(
          { length: endIndex - startIndex + 1 },
          (_, offset) => startIndex + offset,
        )
      : []

  const emptyPlaceholder = (
    <DataTableEmpty
      columns={columns as ReadonlyArray<Column>}
      model={model as unknown as DataTableModel}
      emptyTitle={emptyTitle}
      renderError={renderError}
      renderEmptyMessage={renderEmptyMessage}
    />
  )

  const backToTopViewportStickyTop =
    stickyHeaderTop === undefined ? undefined : stickyHeaderTop

  return (
    <>
      {withBackToTop || floatingActions ? (
        <DataTableBackToTop
          onBackToTop={handleBackToTop}
          scrollAtom={scrollAtom}
          viewportStickyTop={backToTopViewportStickyTop}
          floatingActions={floatingActions}
        />
      ) : undefined}
      <SimpleTable ref={scrollContainerRef} data-empty={isEmpty}>
        <SimpleTableHead $top={stickyHeaderTop} style={{ minWidth }}>
          <DataTableHeader<D, ColumnId, Context>
            columns={columns}
            model={model}
            context={context as Context}
          />
        </SimpleTableHead>
        <SimpleTableBodyScroll style={{ minWidth }}>
          <SimpleTableBody ref={tableBodyRef}>
            {isEmpty ? (
              emptyPlaceholder
            ) : (
              <>
                {topSpacerHeight > 0 ? (
                  <VirtualRowSpacer $height={topSpacerHeight} />
                ) : undefined}
                {visibleRowIndices.map((index) => {
                  const item = data[index]
                  const rowElement = (
                    <DataTableRow
                      key={item ? model.getId(item) : `empty-${index}`}
                      index={index}
                      rowData={item}
                      columns={columns}
                      rowHeight={rowHeight}
                      getRowStyle={getRowStyle}
                      model={model}
                      context={context}
                      renderBackgroundLink={renderBackgroundLink}
                      onRowClick={onRowClick}
                      style={rowStyle}
                    />
                  )

                  if (renderRow && item) {
                    return renderRow({ data: item, element: rowElement })
                  }

                  return rowElement
                })}
                {bottomSpacerHeight > 0 ? (
                  <VirtualRowSpacer $height={bottomSpacerHeight} />
                ) : undefined}
                {hasMore ? (
                  <LoadMoreSentinel ref={loadMoreSentinelRef} />
                ) : undefined}
              </>
            )}
          </SimpleTableBody>
        </SimpleTableBodyScroll>
      </SimpleTable>
    </>
  )
}

const VIRTUAL_ROW_OVERSCAN = 5

const getScrollViewportBounds = (scrollEl: Element | Window) => {
  if (scrollEl instanceof Window) {
    return { top: 0, bottom: scrollEl.innerHeight }
  }

  const rect = scrollEl.getBoundingClientRect()
  return { top: rect.top, bottom: rect.bottom }
}

const computeVirtualRowRange = ({
  anchorEl,
  scrollEl,
  rowCount,
  rowHeight,
  overscan,
}: {
  anchorEl: HTMLElement
  scrollEl: Element | Window
  rowCount: number
  rowHeight: number
  overscan: number
}) => {
  if (rowCount === 0) {
    return { startIndex: 0, endIndex: -1 }
  }

  const bodyRect = anchorEl.getBoundingClientRect()
  const { top: viewportTop, bottom: viewportBottom } =
    getScrollViewportBounds(scrollEl)

  if (bodyRect.bottom < viewportTop) {
    return { startIndex: rowCount, endIndex: rowCount - 1 }
  }

  if (bodyRect.top > viewportBottom) {
    return { startIndex: 0, endIndex: -1 }
  }

  const firstVisibleRow = Math.floor((viewportTop - bodyRect.top) / rowHeight)
  const lastVisibleRow =
    Math.ceil((viewportBottom - bodyRect.top) / rowHeight) - 1

  const startIndex = Math.max(0, firstVisibleRow - overscan)
  const endIndex = Math.min(rowCount - 1, lastVisibleRow + overscan)

  return { startIndex, endIndex }
}

const useVirtualizedRowRange = ({
  anchorRef,
  rowCount,
  rowHeight,
  overscan = VIRTUAL_ROW_OVERSCAN,
  enabled = true,
}: {
  anchorRef: React.RefObject<HTMLElement | null>
  rowCount: number
  rowHeight: number
  overscan?: number
  enabled?: boolean
}) => {
  const [range, setRange] = useState(() => ({
    startIndex: 0,
    endIndex: Math.min(rowCount - 1, overscan * 2),
  }))

  const updateRange = useCallback(() => {
    const anchorEl = anchorRef.current
    if (!anchorEl || !enabled || rowCount === 0) {
      return
    }

    const scrollEl = findScrollableAncestor(anchorEl)
    if (!scrollEl) {
      return
    }

    setRange(
      computeVirtualRowRange({
        anchorEl,
        scrollEl,
        rowCount,
        rowHeight,
        overscan,
      }),
    )
  }, [anchorRef, enabled, overscan, rowCount, rowHeight])

  useEffect(() => {
    if (!enabled) {
      setRange({ startIndex: 0, endIndex: rowCount - 1 })
      return
    }

    const anchorEl = anchorRef.current
    if (!anchorEl) {
      return
    }

    const scrollEl = findScrollableAncestor(anchorEl)
    if (!scrollEl) {
      return
    }

    let rafId = 0
    const scheduleUpdate = () => {
      cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(updateRange)
    }

    scheduleUpdate()

    if (scrollEl instanceof Window) {
      scrollEl.addEventListener('scroll', scheduleUpdate, { passive: true })
      scrollEl.addEventListener('resize', scheduleUpdate, { passive: true })
    } else {
      scrollEl.addEventListener('scroll', scheduleUpdate, { passive: true })
    }

    const resizeObserver = new ResizeObserver(scheduleUpdate)
    resizeObserver.observe(anchorEl)
    if (scrollEl instanceof HTMLElement) {
      resizeObserver.observe(scrollEl)
    }

    return () => {
      cancelAnimationFrame(rafId)
      if (scrollEl instanceof Window) {
        scrollEl.removeEventListener('scroll', scheduleUpdate)
        scrollEl.removeEventListener('resize', scheduleUpdate)
      } else {
        scrollEl.removeEventListener('scroll', scheduleUpdate)
      }
      resizeObserver.disconnect()
    }
  }, [anchorRef, enabled, rowCount, rowHeight, updateRange])

  useEffect(() => {
    updateRange()
  }, [rowCount, updateRange])

  return range
}

const SimpleTable = styled.div.attrs({
  'data-virtuoso-component-table': true,
} as React.HTMLAttributes<HTMLElement>)`
  &[data-empty='true'] {
    display: grid;
    grid-template-rows: auto minmax(0, 1fr);
    min-height: 240px;
  }
`

const SimpleTableHead = styled.div.attrs({
  'data-virtuoso-component-table-head': true,
} as React.HTMLAttributes<HTMLElement>)<{ $top?: number }>`
  ${({ $top }) =>
    $top === undefined
      ? undefined
      : css`
          position: sticky;
          top: ${$top}px;
          z-index: 1;
          background-color: var(--data-table-header-bg, var(--background-2));
        `}

  [data-table-header] {
    position: static;
  }
`

const SimpleTableBodyScroll = styled.div`
  overflow-x: auto;
  min-width: 0;

  ${SimpleTable}[data-empty='true'] & {
    display: grid;
    grid-template-rows: minmax(0, 1fr);
    min-height: 0;
  }
`

const SimpleTableBody = styled.div.attrs({
  'data-virtuoso-component-table-body': true,
} as React.HTMLAttributes<HTMLElement>)`
  ${SimpleTable}[data-empty='true'] & {
    display: grid;
    grid-template-rows: minmax(0, 1fr);
    min-height: 0;
  }
`

const VirtualRowSpacer = styled.div<{ $height: number }>`
  height: ${({ $height }) => $height}px;
  width: 100%;
  pointer-events: none;
`

const LoadMoreSentinel = styled.div`
  height: 1px;
  width: 100%;
`
